import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';
import { t } from 'i18next';

import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { PersistentStore } from '../stores/PersistentStore';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { UIHelper } from './UIHelper';
import { ScreenshotHelper } from './ScreenshotHelper';
import { PconLoginStore } from '../stores/PconLoginStore';
import { ToggleStoreState } from '../stores/ToggleStore';
import { Converter } from './Converter';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { UrlArgumentsStore } from '../stores/UrlArgumentsStore';
import { ArticleActions } from '../actions/ArticleActions';
import { PDFMaker } from './PDFMaker';
import { ArticleStore } from '../stores/ArticleStore';
import { XLSXDocument } from './XLSXDocument';
import { DownloadHelper } from './DownloadHelper';
import { ExportGeometryFormat } from '../constants/ExportGeometryFormats';
import { DialogActions } from '../actions/DialogActions';
import { Stats } from './Stats';
import { ArStore } from '../stores/ArStore';

// Submodules
import { Dict } from '../submodules/utils/Types';
import { downloadBlob } from '../submodules/utils/Download';
import { EventData, awaitOnce } from '../submodules/utils/Promise';
import { ArScreenshot, ArCore } from '../submodules/ar-manager/ArCore';
import { requestArCameraFrame } from '../submodules/app-api/AppAction';

import utils = egr.wcf.utils;

interface ExportMapping {
    format: string;
    url: string;
}

interface MailShareContent {
    image?: string;
    articleList?: string | ArrayBuffer;
    excel?: string;
    cad?: Array<ExportMapping>;
    session?: Array<ExportMapping>;
}

export abstract class MailHelper {
    public static openMailtoLink(address: string, linkArguments?: Dict): void {
        let link: string = 'mailto:' + (address != null ? address : '');
        if (linkArguments != null) {
            link += '?' + Object.keys(linkArguments).map((key: string): string => {
                return key + '=' + encodeURIComponent(linkArguments[key]);
            }).join('&');
        }

        if (
            true &&
            ViewSettingsStore.getBrowser() === 'Safari'
        ) {
            // Note: wrapping `mailto` links inside iframes does not work with safari; more details can be found in the README file
            window.location.href = link;
        } else {
            UIHelper.insertBackgroundFrame(link, 5000);
        }
    }

    public static getMailLink(): string {
        return 'mailto:box-support@easterngraphics.com';
    }

    public static getTextEmail = (shareContent: MailShareContent, shareLinks: Array<ExportMapping>, forZip?: boolean): Promise<string> => {
        let body: string = '';
        let linkInfoNeeded: boolean = false;
        if (shareContent.image != null && !forZip) {
            linkInfoNeeded = true;
            body += '\r\n' + t('Image') + ': ' + shareContent.image + '\r\n';
        }

        if (shareContent.articleList != null && !forZip) {
            linkInfoNeeded = true;
            body += '\r\n' + t('Article List') + ' (PDF): ' + shareContent.articleList + '\r\n';
        }

        if (shareContent.excel != null && !forZip) {
            linkInfoNeeded = true;
            body += '\r\n' + t('Article List') + ' (Excel): ' + shareContent.excel + '\r\n';
        }

        const infoLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
            return (
                shareLink.format === 'OBK' ||
                shareLink.format === 'OBX'
            );
        });

        const cadLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
            return !(
                shareLink.format === 'OBK' ||
                shareLink.format === 'OBX'
            );
        });

        if (infoLinks.length) {
            linkInfoNeeded = true;
            body += '\r\n' + t('Commercial Information') + ':';
            body += '\r\n' + infoLinks.map((value: ExportMapping): string => {
                return value.format + ': ' + value.url;
            }).join('\r\n') + '\r\n';
        }

        if (cadLinks.length) {
            linkInfoNeeded = true;
            body += '\r\n' + t('CAD Symbols') + ':';
            body += '\r\n' + cadLinks.map((value: ExportMapping): string => {
                return value.format + ': ' + value.url;
            }).join('\r\n');
        }

        if (!PconLoginStore.isLoggedIn()) {
            body += '\r\n\r\n ' + ScreenshotHelper.getWatermarkText() + '\r\n';
        }

        if (linkInfoNeeded) {
            body = MailHelper.linkInfo() + body;
        }
        if (forZip) {
            return Promise.resolve<string>(body);
        }
        return Promise.resolve<string>('mailto:?body=' + encodeURIComponent(body));
    };

    public static getHtmlEmail = (shareContent: MailShareContent, shareLinks: Array<ExportMapping>, exportSettings: ToggleStoreState): Promise<any> => {
        // tslint:disable-next-line:max-line-length
        const textTemplate: string = '<div style="cursor:auto;color:#000000;font-size:14px;font-weight:400;line-height:22px;text-align:left;">#TEXT#</div>';
        // tslint:disable-next-line:max-line-length
        const linkTemplate: string = '<div style="cursor:auto;color:#000000;font-size:14px;font-weight:400;line-height:22px;text-align:left;"><a style="color:#7da50b;text-decoration: none;" href="#LINKURL#">#LINKNAME#&nbsp;&raquo;</a></div>';
        let linkInfoNeeded: boolean = false;

        const exportInfo: {
            subject: string;
            body: {
                path: string;
                html: boolean;
            },
            attachments: Array<{
                path: string;
                name: string;
                mimetype: string;
            }>
        } = {
            subject: '',
            body: {
                path: 'body.html',
                html: AppConfigurationStore.getState().os !== 'AndroidOS'
            },
            attachments: []
        };

        let mailContent: string = '';
        if (shareLinks.length) {
            linkInfoNeeded = true;
            const infoLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
                return (
                    shareLink.format === 'OBK' ||
                    shareLink.format === 'OBX'
                );
            });
            const cadLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
                return !(
                    shareLink.format === 'OBK' ||
                    shareLink.format === 'OBX'
                );
            });
            if (infoLinks.length) {
                mailContent += '\r\n<br>' + textTemplate.replace('#TEXT#', t('Commercial Information') + ':') + '\r\n';
                mailContent += '\r\n' + infoLinks.map((value: ExportMapping): string => {
                    return linkTemplate.replace('#LINKURL#', value.url).replace('#LINKNAME#', value.format);
                }).join('\r\n');
            }
            if (cadLinks.length) {
                mailContent += '\r\n<br>' + textTemplate.replace('#TEXT#', t('CAD Symbols') + ':') + '\r\n';
                mailContent += '\r\n' + cadLinks.map((value: ExportMapping): string => {
                    return linkTemplate.replace('#LINKURL#', value.url).replace('#LINKNAME#', value.format);
                }).join('\r\n');
            }
            mailContent += '\r\n<br>';
        }

        if (
            (exportSettings.indexOf('IMAGE') !== -1 && shareContent.image != null) ||
            (shareContent.articleList != null)
        ) {
            mailContent += textTemplate.replace('#TEXT#', t('Attachment') + ':') + '\r\n';
            if (exportSettings.indexOf('IMAGE') !== -1 && shareContent.image != null) {
                mailContent += '\r\n' + textTemplate.replace('#TEXT#', t('Image')) + '\r\n';
            }
            if (shareContent.articleList != null) {
                mailContent += '\r\n' + textTemplate.replace('#TEXT#', t('Article List')) + '\r\n';
            }
        }

        if (linkInfoNeeded) {
            mailContent = textTemplate.replace('#TEXT#', MailHelper.linkInfo()) + mailContent;
        }

        return utils.async.ajax<string>('GET', 'res/mail/mail-template.html', null, { dataType: 'text' }).then(async (html: string): Promise<string> => {
            const zip: JSZip = new JSZip();
            if (AppConfigurationStore.getState().os === 'AndroidOS') {
                zip.file('body.html', Converter.b64EncodeUnicode(await MailHelper.getTextEmail(shareContent, shareLinks, true)), {base64: true});
            } else {
                zip.file('body.html', html.replace('##CONTENT##', mailContent));
            }

            const filename: string = ProjectManagerStore.getProjectName(true);
            if (exportSettings.indexOf('IMAGE') !== -1 && shareContent.image != null) {
                const imageFilename: string = filename + '.png';
                zip.file(imageFilename, shareContent.image, {base64: true});
                exportInfo.attachments.push({
                    name: imageFilename,
                    path: imageFilename,
                    mimetype: 'image/png'
                });
            }

            if (shareContent.articleList != null) {
                const articleListFilename: string = filename + '.pdf';
                zip.file(articleListFilename, shareContent.articleList, {binary: true});
                exportInfo.attachments.push({
                    name: articleListFilename,
                    path: articleListFilename,
                    mimetype: 'application/pdf'
                });
            }

            if (shareContent.excel != null) {
                const excelFileName: string = filename + '.xlsx';
                zip.file(excelFileName, shareContent.excel, {binary: true});
                exportInfo.attachments.push({
                    name: excelFileName,
                    path: excelFileName,
                    mimetype: XlsxPopulate.MIME_TYPE
                });
            }

            zip.file('export.json', JSON.stringify(exportInfo));

            if (UrlArgumentsStore.getState().debug != null) {
                return zip.generateAsync({
                    type: 'blob'
                }).then((content: Blob): string => {
                    downloadBlob(content, 'mail.zip');
                    return 'about:blank';
                });
            }

            return zip.generateAsync({
                type: 'base64'
            }).then((content: string): string => {
                return 'mail-link://?raw=' + content;
            });
        });
    };

    public static async generateMailShareContent(exportSettings: ToggleStoreState, killProcess: () => boolean): Promise<MailShareContent | null> {
        const filename: string = ProjectManagerStore.getProjectName(true);
        const shareContent: MailShareContent = {};
        let itemCount: number = 0;
        if (killProcess != null && killProcess()) {
            return null;
        }

        await ArticleActions.updateArticleList();

        // add Screenshot
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('IMAGE') !== -1) {
            if (ArStore.getState().started) {
                ArStore.core.screenshotType = 'dataurl';
                const promise: Promise<EventData<ArScreenshot, ArCore>> = awaitOnce<ArScreenshot, ArCore>(ArStore.core.eventArScreenshot);
                await requestArCameraFrame();
                const data: EventData<ArScreenshot, ArCore> = await promise;
                if (data.parameter != null && data.parameter.output === 'dataurl') {
                    shareContent.image = Converter.dataUrlToBase64String(data.parameter.data);
                    itemCount++;
                }
            } else {
                shareContent.image = await ScreenshotHelper.getScreenshot({
                    width: 1920,
                    height: 1080,
                    stripUrlScheme: true,
                    renderAsync: true,
                    addWatermark: true,
                    customMimetype: true
                });
                itemCount++;
            }
        }

        // add PDF Article List
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('PDF') !== -1) {
            if (false && true) {
                shareContent.articleList = await PDFMaker.generatePDFAsBuffer();
            } else {
                shareContent.articleList = await PDFMaker.generatePDFAsBase64();
            }
            itemCount++;
        }

        // add Excel Article List
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('EXCEL') !== -1) {
            const xlsx: XLSXDocument = new XLSXDocument(ArticleStore.getState().articleList);
            if (false && true) {
                shareContent.excel = await xlsx.getDocument(UIHelper.showPrices()).then((workbook: any) => {
                    return workbook.outputAsync('arraybuffer');
                });
            } else {
                shareContent.excel = await xlsx.getDocument(UIHelper.showPrices()).then((workbook: any) => {
                    return workbook.outputAsync('base64');
                });
            }
            itemCount++;
        }

        // upload the image and article list if needed (if not in app mode)
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (true || false) {
            // We have to upload the image and article list because
            // it is not possible to attach a pdf and image via a mailto-link
            if (shareContent.image != null) {
                shareContent.image = await DownloadHelper.uploadContentToFileStorage(shareContent.image, filename, PersistentStore.getImageExtension());
            }
            if (shareContent.articleList != null) {
                shareContent.articleList = await DownloadHelper.uploadContentToFileStorage(shareContent.articleList as string, filename, 'pdf');
            }
            if (shareContent.excel != null) {
                shareContent.excel = await DownloadHelper.uploadContentToFileStorage(shareContent.excel as string, filename, 'xlsx');
            }
        }

        // generate the cad files
        const cadSetting: ToggleStoreState = exportSettings.filter((setting) => {
            return setting === '3DS' || setting === 'DWG' || setting === 'SKP' || setting === 'FBX';
        });

        if (cadSetting.length) {
            shareContent.cad = [];
            const exportSettingsArray: Array<Array<string>> = cadSetting.map((value: string): Array<string> => {
                return ExportGeometryFormat[value];
            });
            let exportedGeometries: Array<string> = [];

            if (killProcess != null && killProcess()) {
                return null;
            }

            try {
                exportedGeometries = await DownloadHelper.exportGeometries(exportSettingsArray);
            } catch (e) {
                let label: string = t('The CAD file could not be generated.');
                if (cadSetting.length > 1) {
                    label = t('The CAD files could not be generated.');
                }
                const continueProgress: boolean = await DialogActions.getPromiseDialog(label);
                if (!continueProgress) {
                    return null;
                }
            }

            for (let i: number = 0; i < exportedGeometries.length; i++) {
                if (killProcess != null && killProcess()) {
                    return null;
                }

                try {
                    shareContent.cad.push({
                        format: cadSetting[i],
                        url: await DownloadHelper.uploadUrlToFileStorage(exportedGeometries[i], filename)
                    });
                } catch (e) {
                    const continueProgress: boolean = await DialogActions.getPromiseDialog(
                        t('The CAD file could not be generated.') + ' (' + cadSetting[i] + ')'
                    );
                    if (!continueProgress) {
                        return null;
                    }
                }
            }
            itemCount += shareContent.cad.length;
        }

        // Generate the store the session export files
        const sessionExports: Array<ExportMapping> = [];

        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('OBX') !== -1) {
            sessionExports.push(
                await DownloadHelper.exportObx().then(
                    async (url: string) => {
                        return {
                            format: 'OBX',
                            url: await DownloadHelper.uploadUrlToFileStorage(url, filename)
                        };
                    }
                )
            );
            itemCount++;
        }

        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('OBK') !== -1) {
            sessionExports.push(
                await DownloadHelper.exportObk().then(
                    async (url: string) => {
                        return {
                            format: 'OBK',
                            url: await DownloadHelper.uploadUrlToFileStorage(url, filename)
                        };
                    }
                )
            );
            itemCount++;
        }
        shareContent.session = sessionExports;
        if (itemCount === 0) {
            return null;
        }
        return shareContent;
    }

    public static generateShareLink(exportSettings: ToggleStoreState, killProcess: () => boolean): Promise<string | null> {
        return this.generateMailShareContent(exportSettings, killProcess).then(async (shareContent: MailShareContent | null): Promise<string | null> => {
            if (shareContent == null) {
                return null;
            }
            let shareLinks: Array<ExportMapping> = [];
            if (shareContent.cad != null) {
                shareLinks = shareLinks.concat(shareContent.cad);
            }

            if (shareContent.session != null) {
                shareLinks = shareLinks.concat(shareContent.session);
            }

            // send statistic data
            shareLinks.map((value: ExportMapping): void => {
                Stats.sendEvent({
                    category: 'Send',
                    action: 'e-mail',
                    label: value.format
                });
            });

            if (shareContent.articleList != null) {
                Stats.sendEvent({
                    category: 'Send',
                    action: 'e-mail',
                    label: 'PDF'
                });
            }

            if (shareContent.image != null) {
                Stats.sendEvent({
                    category: 'Send',
                    action: 'e-mail',
                    label: PersistentStore.getState().imageFormat
                });
            }
            // end stats

            return MailHelper.getTextEmail(shareContent, shareLinks);
        });
    }

    protected static linkInfo(): string {
        const currentDate: Date = new Date();
        currentDate.setMonth(currentDate.getMonth() + 1);
        return (
            t('You can download the following files.') +
            ' ' +
            t('These links are valid until {{date}}.', {date: currentDate.toLocaleDateString(PersistentStore.getState().language)}) +
            '\r\n'
        );
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/MailHelper.ts